/* ============================================================================
   CABINET v2 — API contracts. The FROZEN interface between the surfaces and
   the gateway. Movement 2 surfaces build against `CabinetApi` (mock now, real
   endpoints in A11); the shapes here do not change without a foundation bump.
   Self-contained on purpose so parallel agents need nothing else.
   ========================================================================== */

/* ---------- shared ---------- */
export type Severity = 'ok' | 'warn' | 'crit';
export type Tone = 'default' | 'ok' | 'warn' | 'crit';

/**
 * A data-driven instrument. Surfaces render these through one dispatcher so
 * every domain's vitals share the instrument family. Maps 1:1 onto A2's
 * components. `label` becomes the card cap; `tag` the corner tag.
 */
export type InstrumentSpec =
  | { kind: 'dial'; label: string; value: number; max: number; unit?: string; sub?: string; tag?: string; tagTone?: Severity }
  | { kind: 'rule'; label: string; readout: string; unit?: string; points?: number[]; markerPct?: number; tag?: string; tagTone?: Severity }
  | { kind: 'ring'; label: string; value: number; max: number; center?: string; sub?: string; tag?: string; tagTone?: Severity }
  | { kind: 'gauge'; label: string; value: number; max: number; threshold?: number; leftLabel?: string; rightLabel?: string; tag?: string; tagTone?: Severity }
  | { kind: 'stat'; label: string; big: string; unit?: string; sub?: string; tone?: Tone; points?: number[]; pointsColor?: string; tag?: string; tagTone?: Severity };

/* ---------- chats ---------- */
export type MessageRole = 'user' | 'assistant' | 'system';
export type MessagePart =
  | { type: 'text'; text: string }
  | { type: 'tool-run'; toolId: string; name: string; input: unknown; output?: string; isError?: boolean; done: boolean; at?: string }
  | { type: 'notice'; level: 'info' | 'warn'; text: string }
  | { type: 'widget'; widgetType: string; data: unknown }
  | { type: 'approval'; packet: ApprovalPacket }
  /** A composer image attachment — `id` names the file behind GET /api/attachments/:id (never inlined here). Mirrors server/src/gateway/fold.ts's MessagePart 1:1 (hand-synced, see chat.ts). */
  | { type: 'image'; id: string; mediaType: string };

export interface ApprovalPacket {
  id: string; tier: number; action: string; payload: string; reasoning: string;
  confidence: number | null; reversibility: string | null; chatId: string | null; expiresAt: string;
}

export interface ChatSummary {
  id: string; title: string | null; model_override: string | null;
  archived: number; updated_at: string; messages: number;
  /** short preview / produced-artifact hint for the archive */
  preview?: string;
}
export interface ChatMessage { id: string; role: MessageRole; parts: MessagePart[]; created_at: string; author?: string | null; }

/* ---------- today ---------- */
export interface AttentionAction { label: string; intent: string; primary?: boolean; }
export interface AttentionItem {
  id: string; severity: Exclude<Severity, 'ok'>; badge?: string;
  title: string; meta?: string; detail: string; actions: AttentionAction[];
}
export interface OvernightNote { count: number; summary: string; }
/** The real morning-briefing narrative, durably read from sys-briefing — null when the job has never fired. */
export interface BriefingOutput { at: string; isCurrent: boolean; narrative: string; }
/** The real evening-checkin output, durably read from sys-checkin — null when the job has never fired. */
export interface CheckinOutput { at: string; isCurrent: boolean; vitals: InstrumentSpec[]; prompt: string; }
export interface TodayView {
  greeting: string;        // "Good morning, Ben." — the fallback/empty-state template, used only when briefing is null
  greetingAccent?: string; // italic brass clause, e.g. "A quiet day"
  read: string;            // the fallback template's supporting line
  attention: AttentionItem[];
  vitals: InstrumentSpec[];
  overnight: OvernightNote | null;
  sweptAt: string;         // ISO
  briefing: BriefingOutput | null;
  checkin: CheckinOutput | null;
}

/* ---------- domains ---------- */
export type DomainId = 'nutrition' | 'training' | 'health' | 'money' | 'admin' | 'people' | 'play';
export interface DomainMeta { id: DomainId; label: string; }
export const DOMAINS: DomainMeta[] = [
  { id: 'nutrition', label: 'Nutrition' },
  { id: 'training', label: 'Training' },
  { id: 'health', label: 'Health' },
  { id: 'money', label: 'Money' },
  { id: 'admin', label: 'Admin' },
  { id: 'people', label: 'People' },
  { id: 'play', label: 'Play' },
];
export interface LogEntry { id: string; at: string; text: string; meta?: string; }
export interface DomainView {
  id: DomainId; label: string;
  instruments: InstrumentSpec[];
  narrative: string;   // the agent's written read of this domain (Cabinet's voice)
  log: LogEntry[];
}

/* ---------- ops (the trust surface) ---------- */
export type OpsKind = 'user' | 'heartbeat' | 'cron';
export interface OpsEntry {
  id: string; at: string; tool: string; action: string; reason: string;
  kind: OpsKind; chatId: string | null;
  reversible: boolean; diff?: string;
  reverted?: boolean;
}
export interface OpsFeed { entries: OpsEntry[]; }

/* ---------- brain: memory + recall ---------- */
export interface MemoryFile { name: string; content: string; updatedAt: string | null; editable: boolean; }
export interface MemoryLesson { id: number; text: string; domain: string | null; confidence: number; }
export interface MemoryView { files: MemoryFile[]; lessons: MemoryLesson[]; }

export type RecallSource = 'fact' | 'episodic' | 'chat' | 'lesson' | 'document';
export interface RecallResult {
  source: RecallSource; title: string; snippet: string;
  provenance: string; score: number; ref: string;
}
export interface RecallResponse { query: string; results: RecallResult[]; }

/* ---------- settings ----------
   Mirrors server/src/domains/settings.ts + gateway/settingsRoutes.ts.

   Every field here is a plaintext value that is safe to print, echo and
   screenshot. That is the membership rule for the table, not an accident of
   the current contents — a value that needs hiding does not belong in it.

   Resolution order is DB row → environment variable → built-in default, and
   `source` reports which of the three won. Rendering that is the whole job: a
   settings page whose edit silently loses to an invisible env var is the worst
   bug this surface can have. */
export type SettingType = 'enum' | 'origin' | 'text';
export type SettingSource = 'db' | 'env' | 'default';

export interface SettingView {
  key: string;
  /** Group heading — one integration's settings stay together. */
  group: string;
  label: string;
  /** What it does, in outcome terms. Rendered under the control. */
  description: string;
  type: SettingType;
  /** Allowed values, for `enum` only. */
  options?: string[];
  /** Used when neither a stored row nor the environment supplies a value. */
  default: string;
  /** The legacy environment variable this setting outranks, when there is one. */
  envVar?: string;
  /** True when the process reads this once at boot, so a save isn't live yet. */
  restartRequired?: boolean;
  /** The value actually in force. */
  value: string;
  source: SettingSource;
  /** SQLite `datetime('now')` — naive UTC. Non-null only when `source` is 'db'. */
  updated_at: string | null;
  /** The environment's value, when the variable is set — whether or not it won. */
  env_value: string | null;
}

/* ---------- money: the ledger ----------
   Mirrors server/src/domains/money.ts and server/src/gateway/moneyRoutes.ts.
   Two sign conventions travel with this data and both are load-bearing:
     · a transaction `amount` is POSITIVE when money LEFT the account;
     · `credit` and `loans` in NetWorth are POSITIVE meaning "owed", and
       net_worth is already cash + investments − credit − loans.
   Anything rendering these inverts them at its peril. */
export type AccountStatus = 'active' | 'login_required' | 'error' | 'revoked';

export interface FinancialAccount {
  id: number;
  account_id: string;
  institution_name: string | null;
  name: string | null;
  mask: string | null;
  type: string | null;
  subtype: string | null;
  current_balance: number | null;
  available_balance: number | null;
  limit_amount: number | null;
  balance_as_of: string | null;
  item_status: AccountStatus;
  /** 0/1 — hidden accounts stay in the ledger but drop out of every rollup. */
  hidden: number;
}

/**
 * `accounts_counted` vs `accounts_total` is not decoration: an account with no
 * current balance (a broken item, a fresh link mid-backfill) silently drops
 * out of the sum, and the result looks exactly like a real drop in net worth.
 * Every renderer of `net_worth` renders the ratio and `stalest_balance_at`
 * with it.
 */
export interface NetWorth {
  cash: number;
  /** Positive = owed. */
  credit: number;
  investments: number;
  /** Positive = owed. */
  loans: number;
  net_worth: number;
  accounts_counted: number;
  accounts_total: number;
  stalest_balance_at: string | null;
}

export interface MoneyTransaction {
  transaction_id: string;
  /** YYYY-MM-DD, local to the account. */
  date: string;
  /** Positive = spent, negative = received. */
  amount: number;
  merchant: string | null;
  category_primary: string | null;
  category_detailed: string | null;
  account: string | null;
  mask: string | null;
  /** 0/1 — provisional; the amount and merchant can both still change. */
  pending: number;
}

/** `spent` is POSITIVE dollars out; the sign flip happens once, server-side. */
export interface CategorySpend { category: string; detailed_top: string | null; spent: number; txns: number; }

export interface Holding {
  ticker: string | null; security: string | null; type: string | null;
  quantity: number | null; price: number | null; value: number | null;
  cost_basis: number | null; account: string | null; institution: string | null;
}

export interface NetWorthSnapshot {
  local_day: string;
  cash: number; credit: number; investments: number; loans: number; net_worth: number;
  accounts_counted: number; accounts_total: number;
}
export interface SpendDay { local_day: string; spent: number; txns: number; }
export interface MoneyTrend { net_worth: NetWorthSnapshot[]; spend_by_day: SpendDay[]; }

export interface MoneySummary {
  linked_institutions: number;
  /** Accounts whose institution is anything other than `active` — first-class, not a footnote. */
  needs_attention: { institution: string | null; status: AccountStatus; error: string | null }[];
  last_synced_at: string | null;
  net_worth: NetWorth;
  window_days: number;
  total_spent: number;
  by_category: CategorySpend[];
  accounts: FinancialAccount[];
}

/* ---------- health / presence ---------- */
export type PresenceState = 'idle' | 'working' | 'thinking' | 'offline';
export interface HealthInfo { ok: boolean; authMode: string; presence: PresenceState; presenceMeta: string; }

/* ---------- usage (Ops surface: "why did we spike" / "are we near a wall") ---------- */
export interface UsageDay {
  day: string; model: string;
  input: number; output: number; cache_read: number; cache_write: number;
  cost_usd: number; turns: number;
}
export interface UsageView { authMode: string; byDay: UsageDay[]; }

export type UsageWindowId = '5h' | '24h' | '7d';
export interface UsageWindow {
  window: UsageWindowId;
  input: number; output: number; cache_read: number; cache_write: number;
  cost_usd: number; turns: number;
  /** cache_read / cache_write, rounded to 2dp. null when there's been no write to divide by yet. */
  cacheReadWriteRatio: number | null;
}
export interface UsageRollingView { authMode: string; windows: UsageWindow[]; }

/* ---------- latency (Ops surface: "was that turn slow") ----------
   Mirrors runtime/perf.ts, which was cut back on 2026-08-11 from a per-phase
   span table (~39 rows a turn, 15k rows in ten days, read by nobody) to one
   row per turn. The per-phase breakdown was the right tool for the
   investigation that prompted it and the wrong thing to keep forever; what
   survives is the four numbers that say whether a turn was slow and roughly
   where it went. All times are milliseconds. */
export interface PerfSpread { p50: number; p95: number; max: number; }
export interface PerfTurnSummary {
  turnId: string;
  chatId: string | null;
  sessionKind: string | null;
  model: string | null;
  startedAt: string;
  totalMs: number | null;
  ttfTextMs: number | null;
  steps: number;
  toolCalls: number;
}
export interface PerfView {
  enabled: boolean;
  window: string;
  turns: number;
  totalMs: PerfSpread | null;
  ttfTextMs: PerfSpread | null;
  avgSteps: number;
  avgToolCalls: number;
  recent: PerfTurnSummary[];
}

/* ============================================================================
   The single interface both the mock and the real (fetch) client implement.
   Surfaces depend ONLY on this.
   ========================================================================== */
export interface CabinetApi {
  health(): Promise<HealthInfo>;
  today(): Promise<TodayView>;
  domain(id: DomainId): Promise<DomainView>;
  ops(filter?: { kind?: OpsKind; domain?: string }): Promise<OpsFeed>;
  revertOp(id: string): Promise<{ ok: boolean }>;
  usage(): Promise<UsageView>;
  usageRolling(): Promise<UsageRollingView>;
  perf(hours?: number): Promise<PerfView>;
  memory(): Promise<MemoryView>;
  saveMemoryFile(name: string, content: string): Promise<{ ok: boolean }>;
  recall(query: string): Promise<RecallResponse>;
  chats(): Promise<{ chats: ChatSummary[] }>;
  createChat(): Promise<{ id: string }>;
  deleteChat(chatId: string): Promise<{ ok: boolean }>;
  /** `live` — a turn is executing on this chat server-side right now
   *  (reattach-on-load; optional so the mock backend can ignore it). */
  messages(chatId: string): Promise<{ messages: ChatMessage[]; live?: boolean }>;
  command(intent: string): Promise<{ chatId: string }>;

  /* ---- settings: plaintext both ways, and echoed back normalised ---- */
  settings(): Promise<{ settings: SettingView[] }>;
  /** Returns the RESOLVED view, not the submitted string — the server normalises. Render what comes back. */
  saveSetting(key: string, value: string): Promise<{ setting: SettingView }>;
  /**
   * Stop overriding: drops the stored row so the value falls back to the
   * environment variable or the built-in default. Deliberately not the same
   * operation as saving the old value back, which would leave a row that keeps
   * outranking a future .env change forever.
   */
  revertSetting(key: string): Promise<{ setting: SettingView }>;

  /* ---- money: the ledger ---- */
  /** Every account (hidden included) and the net worth computed from them, in one read. */
  moneyAccounts(): Promise<{ accounts: FinancialAccount[]; net_worth: NetWorth }>;
  moneySummary(days?: number): Promise<MoneySummary>;
  moneyTransactions(opts?: { days?: number; limit?: number }): Promise<{ transactions: MoneyTransaction[] }>;
  moneyTrend(days?: number): Promise<MoneyTrend>;
  moneyCategories(days?: number): Promise<{ categories: CategorySpend[] }>;
  moneyHoldings(): Promise<{ holdings: Holding[] }>;
  /** Hidden accounts stay in the ledger and drop out of every rollup. */
  moneySetAccountHidden(accountId: number, hidden: boolean): Promise<{ ok: boolean; id: number; hidden: boolean }>;
}
